import { useState, useEffect } from 'react'
import {
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Link2,
  FileText,
  Shield,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  Info,
  Upload,
  X,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import api from '@/services/api'

// ── Interfaces ──────────────────────────────────────────────────────────────

type AuthType = 'none' | 'basic' | 'oauth' | 'api_key'
type SyncFrequency = 'hourly' | 'daily' | 'weekly'
type SourceStatus = 'connected' | 'syncing' | 'error' | 'pending'

interface ConnectedSource {
  id: string
  url: string
  name: string
  authType: AuthType
  syncFrequency: SyncFrequency
  status: SourceStatus
  lastSyncTime: string | null
  documentCount: number
  errorMessage?: string
  createdAt: string
}

interface AddSourceForm {
  url: string
  name: string
  authType: AuthType
  username: string
  password: string
  apiKey: string
  oauthClientId: string
  oauthClientSecret: string
  oauthTokenUrl: string
  syncFrequency: SyncFrequency
}

// ── Sample Data ─────────────────────────────────────────────────────────────

const sampleSources: ConnectedSource[] = [
  {
    id: '1',
    url: 'https://hondaaircraft.techpubs.com/ha420',
    name: 'Honda Aircraft ALIM Portal',
    authType: 'oauth',
    syncFrequency: 'daily',
    status: 'connected',
    lastSyncTime: '2026-02-19T06:30:00Z',
    documentCount: 1247,
    createdAt: '2025-11-15T10:00:00Z',
  },
  {
    id: '2',
    url: 'https://ge-honda-aero.com/hf120/maintenance',
    name: 'GE Honda HF120 Engine Manuals',
    authType: 'api_key',
    syncFrequency: 'weekly',
    status: 'connected',
    lastSyncTime: '2026-02-17T14:22:00Z',
    documentCount: 483,
    createdAt: '2025-12-01T08:00:00Z',
  },
  {
    id: '3',
    url: 'https://garmin.com/aviation/database/g3000',
    name: 'Garmin G3000 Avionics Documentation',
    authType: 'basic',
    syncFrequency: 'daily',
    status: 'syncing',
    lastSyncTime: '2026-02-19T08:00:00Z',
    documentCount: 312,
    createdAt: '2026-01-10T12:00:00Z',
  },
  {
    id: '4',
    url: 'https://parts.hondaaircraft.com/ipc',
    name: 'Honda IPC Parts Catalog',
    authType: 'oauth',
    syncFrequency: 'daily',
    status: 'error',
    lastSyncTime: '2026-02-18T22:15:00Z',
    documentCount: 890,
    errorMessage: 'OAuth token expired. Re-authenticate to resume sync.',
    createdAt: '2025-10-20T09:30:00Z',
  },
  {
    id: '5',
    url: 'https://faa.gov/regulations/ads/ha420',
    name: 'FAA Airworthiness Directives — HA-420',
    authType: 'none',
    syncFrequency: 'weekly',
    status: 'pending',
    lastSyncTime: null,
    documentCount: 0,
    createdAt: '2026-02-18T16:00:00Z',
  },
]

const emptyForm: AddSourceForm = {
  url: '',
  name: '',
  authType: 'none',
  username: '',
  password: '',
  apiKey: '',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthTokenUrl: '',
  syncFrequency: 'daily',
}

// ── Component ───────────────────────────────────────────────────────────────

export function ConnectedSourcesPage() {
  const [sources, setSources] = useState<ConnectedSource[]>(sampleSources)
  const [loading, setLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [form, setForm] = useState<AddSourceForm>({ ...emptyForm })
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [showOauthSecret, setShowOauthSecret] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchSources = async () => {
      setLoading(true)
      try {
        const data = await api.get<ConnectedSource[]>('/api/documents/sources')
        if (data && Array.isArray(data)) {
          setSources(data)
        }
      } catch {
        // Use sample data on API error
      } finally {
        setLoading(false)
      }
    }
    fetchSources()
  }, [])

  const handleAddSource = async () => {
    if (!form.url || !form.name) return

    setSubmitting(true)
    const newSource: ConnectedSource = {
      id: String(Date.now()),
      url: form.url,
      name: form.name,
      authType: form.authType,
      syncFrequency: form.syncFrequency,
      status: 'pending',
      lastSyncTime: null,
      documentCount: 0,
      createdAt: new Date().toISOString(),
    }

    try {
      await api.post('/api/documents/web-source', {
        url: form.url,
        name: form.name,
        authType: form.authType,
        syncFrequency: form.syncFrequency,
        credentials: {
          username: form.username || undefined,
          password: form.password || undefined,
          apiKey: form.apiKey || undefined,
          oauthClientId: form.oauthClientId || undefined,
          oauthClientSecret: form.oauthClientSecret || undefined,
          oauthTokenUrl: form.oauthTokenUrl || undefined,
        },
      })
    } catch {
      // Continue with local state update
    }

    setSources([...sources, newSource])
    setForm({ ...emptyForm })
    setShowAddForm(false)
    setSubmitting(false)
  }

  const handleDeleteSource = async (id: string) => {
    try {
      await api.delete(`/api/documents/sources/${id}`)
    } catch {
      // Continue with local state update
    }
    setSources(sources.filter((s) => s.id !== id))
  }

  const handleSyncSource = async (id: string) => {
    setSources(
      sources.map((s) =>
        s.id === id ? { ...s, status: 'syncing' as SourceStatus } : s
      )
    )
    try {
      await api.post(`/api/documents/sources/${id}/sync`)
    } catch {
      // Revert status on error
      setSources(
        sources.map((s) =>
          s.id === id ? { ...s, status: 'error' as SourceStatus, errorMessage: 'Sync failed. Please try again.' } : s
        )
      )
    }
  }

  const getStatusIcon = (status: SourceStatus) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />
      case 'syncing':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />
    }
  }

  const getStatusBadge = (status: SourceStatus) => {
    switch (status) {
      case 'connected':
        return <Badge variant="success">Connected</Badge>
      case 'syncing':
        return <Badge variant="info">Syncing</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      case 'pending':
        return <Badge variant="warning">Pending</Badge>
    }
  }

  const getAuthLabel = (authType: AuthType) => {
    switch (authType) {
      case 'none':
        return 'No Auth'
      case 'basic':
        return 'Basic Auth'
      case 'oauth':
        return 'OAuth 2.0'
      case 'api_key':
        return 'API Key'
    }
  }

  const formatSyncTime = (time: string | null) => {
    if (!time) return 'Never'
    const date = new Date(time)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Connected Sources</h1>
          <p className="text-gray-600">Manage web-based manual and documentation connections</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? (
            <>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Add Source
            </>
          )}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-blue-600 shrink-0" />
            <div>
              <p className="font-medium text-blue-900">
                Connect to your organization's web-based manual system
              </p>
              <p className="text-sm text-blue-700">
                Voltaire can sync documentation from web portals including Honda Aircraft ALIM, GE Honda engine manuals,
                Garmin avionics docs, and regulatory sources. PDF upload remains available as a fallback for offline documents.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sources</p>
                <p className="text-2xl font-bold">{sources.length}</p>
              </div>
              <Globe className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Connected</p>
                <p className="text-2xl font-bold text-green-600">
                  {sources.filter((s) => s.status === 'connected').length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Documents</p>
                <p className="text-2xl font-bold">
                  {sources.reduce((sum, s) => sum + s.documentCount, 0).toLocaleString()}
                </p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Errors</p>
                <p className="text-2xl font-bold text-red-600">
                  {sources.filter((s) => s.status === 'error').length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Source Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Add New Source
            </CardTitle>
            <CardDescription>
              Connect to a web-based documentation system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Source Name</label>
                  <Input
                    placeholder="Honda Aircraft ALIM Portal"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">URL</label>
                  <Input
                    type="url"
                    placeholder="https://hondaaircraft.techpubs.com/ha420"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Authentication Type</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={form.authType}
                    onChange={(e) => setForm({ ...form, authType: e.target.value as AuthType })}
                  >
                    <option value="none">None (Public)</option>
                    <option value="basic">Basic Auth (Username/Password)</option>
                    <option value="oauth">OAuth 2.0</option>
                    <option value="api_key">API Key</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Sync Frequency</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={form.syncFrequency}
                    onChange={(e) => setForm({ ...form, syncFrequency: e.target.value as SyncFrequency })}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>

              {/* Basic Auth Fields */}
              {form.authType === 'basic' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Username</label>
                    <Input
                      placeholder="username"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Password</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* API Key Fields */}
              {form.authType === 'api_key' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">API Key</label>
                  <div className="relative">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      placeholder="sk-..."
                      value={form.apiKey}
                      onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* OAuth Fields */}
              {form.authType === 'oauth' && (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Client ID</label>
                      <Input
                        placeholder="client_id"
                        value={form.oauthClientId}
                        onChange={(e) => setForm({ ...form, oauthClientId: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Client Secret</label>
                      <div className="relative">
                        <Input
                          type={showOauthSecret ? 'text' : 'password'}
                          placeholder="client_secret"
                          value={form.oauthClientSecret}
                          onChange={(e) => setForm({ ...form, oauthClientSecret: e.target.value })}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowOauthSecret(!showOauthSecret)}
                        >
                          {showOauthSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Token URL</label>
                    <Input
                      type="url"
                      placeholder="https://auth.example.com/oauth/token"
                      value={form.oauthTokenUrl}
                      onChange={(e) => setForm({ ...form, oauthTokenUrl: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false)
                    setForm({ ...emptyForm })
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddSource} disabled={submitting || !form.url || !form.name}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 className="mr-2 h-4 w-4" />
                      Connect Source
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sources List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading connected sources...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {sources.map((source) => (
            <Card
              key={source.id}
              className={`transition-colors ${
                source.status === 'error' ? 'border-red-200' :
                source.status === 'syncing' ? 'border-blue-200' :
                source.status === 'pending' ? 'border-yellow-200' :
                ''
              }`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="mt-1 shrink-0">
                      {getStatusIcon(source.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{source.name}</h3>
                        {getStatusBadge(source.status)}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-sm text-gray-500">
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{source.url}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          {getAuthLabel(source.authType)}
                        </span>
                        <span className="flex items-center gap-1">
                          <RefreshCw className="h-3 w-3" />
                          {source.syncFrequency.charAt(0).toUpperCase() + source.syncFrequency.slice(1)}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {source.documentCount.toLocaleString()} documents
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last sync: {formatSyncTime(source.lastSyncTime)}
                        </span>
                      </div>
                      {source.status === 'error' && source.errorMessage && (
                        <div className="mt-2 flex items-start gap-1.5 rounded bg-red-50 p-2 text-xs text-red-700">
                          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                          {source.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSyncSource(source.id)}
                      disabled={source.status === 'syncing'}
                    >
                      <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${source.status === 'syncing' ? 'animate-spin' : ''}`} />
                      Sync
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleDeleteSource(source.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {sources.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Globe className="h-12 w-12 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No sources connected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Connect to a web-based manual system to get started.
                </p>
                <Button className="mt-4" onClick={() => setShowAddForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Source
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* PDF Upload Fallback */}
      <Card className="border-dashed">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center text-center sm:flex-row sm:text-left sm:justify-between">
            <div className="flex items-start gap-3">
              <Upload className="mt-0.5 h-5 w-5 text-gray-400 shrink-0" />
              <div>
                <p className="font-medium text-gray-700">PDF Upload (Fallback)</p>
                <p className="text-sm text-gray-500">
                  For offline manuals not available through web portals, use the manual PDF upload on the Manuals page.
                </p>
              </div>
            </div>
            <Button variant="outline" className="mt-3 sm:mt-0 shrink-0">
              <Key className="mr-2 h-4 w-4" />
              Go to Manuals
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
